import axios from 'axios';
import {apiBaseUrl, apiDefaultTimeout} from '@config';
import {authRequestHandler} from '@api/middleware/auth';
import {
  errorsRequestHandler,
  errorsResponseHandler,
  errorsRejectHandler,
} from '@api/middleware/errors';
import {logout, setTokens} from '@store/slices/users';

// для избежания зацикливания используем ленивую загрузку store
let storeInstance = null;
export const setStore = store => {
  storeInstance = store;
};

const apiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: apiDefaultTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';

// Флаг для отслеживания процесса обновления токена
let isRefreshing = false;
// Очередь запросов, которые нужно повторить после обновления токена
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor для обработки 401 ошибки и обновления токена
apiClient.interceptors.response.use(
  response => errorsResponseHandler(response, storeInstance),
  async error => {
    const originalRequest = error.config;

    // Если ошибка 401 и это не запрос на обновление токена
    console.log(error.response.status);
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== 'auth/refresh_token' &&
      !originalRequest._isRefreshRequest
    ) {
      console.log(error.response.status);
      if (isRefreshing) {
        // Если уже происходит обновление, добавляем запрос в очередь
        return new Promise((resolve, reject) => {
          failedQueue.push({resolve, reject});
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // Если нет refresh token, вызываем стандартный обработчик ошибок
        isRefreshing = false;
        return errorsRejectHandler(error, storeInstance);
      }

      try {
        // Делаем запрос на обновление токена
        const refreshResponse = await axios.post(
          `${apiBaseUrl}auth/refresh_token`,
          {refresh_token: refreshToken},
          {
            _isRefreshRequest: true, // Помечаем как запрос обновления
            skipErrorHandler: true, // Пропускаем стандартные обработчики ошибок
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          },
        );

        const {access_token, refresh_token} = refreshResponse.data;

        // Сохраняем новые токены
        localStorage.setItem('authToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);

        if (storeInstance) {
          storeInstance.dispatch(setTokens({access_token, refresh_token}));
        }

        // Обновляем заголовок оригинального запроса
        originalRequest.headers.Authorization = `Bearer ${access_token}`;

        // Обрабатываем очередь ожидающих запросов
        processQueue(null, access_token);

        // Повторяем оригинальный запрос
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Если обновление не удалось, очищаем очередь и вызываем стандартный обработчик
        processQueue(refreshError, null);
        if (storeInstance) {
          storeInstance.dispatch(logout());
        }
        return errorsRejectHandler(error, storeInstance);
      } finally {
        isRefreshing = false;
      }
    }

    // Для всех других ошибок используем стандартный обработчик
    return errorsRejectHandler(error, storeInstance);
  },
);

apiClient.interceptors.request.use(request => authRequestHandler(request, storeInstance));
apiClient.interceptors.request.use(request =>
  errorsRequestHandler(request, storeInstance),
);

export default apiClient;
